import React from 'react';
import styled from 'styled-components';
import { FaGithub, FaLinkedin, FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Footer = () => {
  return (
    <FooterWrapper>
      <div className="container">
        <div className="footer-content">
          <motion.div 
            className="footer-logo"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3>Akash Agarwal</h3>
            <p>Data Engineer | Problem Solver | Tech Enthusiast</p>
          </motion.div>
          
          <motion.div 
            className="footer-links"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#home" className="link-hover">Home</a></li>
              <li><a href="#about" className="link-hover">About</a></li>
              <li><a href="#experience" className="link-hover">Experience</a></li>
              <li><a href="#certifications" className="link-hover">Certifications</a></li>
              <li><a href="#recommendations" className="link-hover">Recommendations</a></li>
              <li><a href="#contact" className="link-hover">Contact</a></li>
            </ul>
          </motion.div>
          
          <motion.div 
            className="footer-contact"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4>Contact Info</h4>
            <ul>
              <li className="contact-item">
                <FaEnvelope className="contact-icon" />
                <a href="mailto:agarwalakash491@gmail.com" className="link-hover">agarwalakash491@gmail.com</a>
              </li>
              <li className="contact-item">
                <FaWhatsapp className="contact-icon" />
                <a href="https://wa.me/918999194346" target="_blank" rel="noopener noreferrer" className="link-hover">Chat on WhatsApp</a>
              </li>
              <li className="contact-item">
                <FaMapMarkerAlt className="contact-icon" />
                <span>Pune, India</span>
              </li>
            </ul>
          </motion.div>
          
          <motion.div 
            className="footer-social"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4>Connect With Me</h4>
            <p>Feel free to reach out for collaborations or just a friendly hello!</p>
            <div className="social-links">
              <a 
                href="https://github.com/Akash491" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="GitHub"
                className="social-link"
              >
                <FaGithub />
              </a>
              <a 
                href="https://www.linkedin.com/in/agarwalakash491/" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="LinkedIn"
                className="social-link"
              >
                <FaLinkedin />
              </a>
              <a 
                href="https://wa.me/918999194346" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="WhatsApp"
                className="social-link"
              >
                <FaWhatsapp />
              </a>
              <a 
                href="mailto:agarwalakash491@gmail.com" 
                aria-label="Email"
                className="social-link"
              >
                <FaEnvelope />
              </a>
            </div>
          </motion.div>
        </div>
        
        <motion.div 
          className="footer-bottom"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <p>&copy; {new Date().getFullYear()} Akash Agarwal. All rights reserved.</p>
          <div className="footer-legal">
            <a href="#" className="footer-legal-link">Privacy Policy</a>
            <span className="divider">•</span>
            <a href="#" className="footer-legal-link">Terms of Service</a>
          </div>
        </motion.div>
      </div>
    </FooterWrapper>
  );
};

const FooterWrapper = styled.footer`
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.light};
  padding: 5rem 0 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0) 70%);
    top: -300px;
    right: -300px;
    z-index: 0;
    pointer-events: none;
  }

  .footer-content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 3rem;
    margin-bottom: 4rem;
    position: relative;
    z-index: 1;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      grid-template-columns: 1fr;
      text-align: left;
    }
  }


  .footer-logo {
    h3 {
      color: ${({ theme }) => theme.colors.light};
      font-size: 1.8rem;
      margin-bottom: 1.5rem;
      font-weight: 700;
      position: relative;
      display: inline-block;
      
      &::after {
        content: '';
        position: absolute;
        width: 50px;
        height: 3px;
        background: ${({ theme }) => theme.colors.primary};
        bottom: -10px;
        left: 0;
        border-radius: 2px;
      }
    }

    p {
      line-height: 1.8;
      margin: 1.5rem 0 0;
      color: ${({ theme }) => theme.colors.light};
      opacity: 0.8;
      max-width: 280px;
      
      @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        max-width: 100%;
      }
    }
  }


  h4 {
    color: ${({ theme }) => theme.colors.light};
    font-size: 1.3rem;
    margin-bottom: 1.8rem;
    position: relative;
    display: inline-block;
    font-weight: 600;

    &::after {
      content: '';
      position: absolute;
      width: 40px;
      height: 3px;
      background: ${({ theme }) => theme.colors.primary};
      bottom: -10px;
      left: 0;
      border-radius: 2px;
    }
  }


  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;

    li {
      margin-bottom: 0.8rem;
      transition: all 0.3s ease;

      a {
        color: ${({ theme }) => theme.colors.light};
        opacity: 0.8;
        text-decoration: none;
        transition: all 0.3s ease;
        position: relative;
        display: inline-block;
        padding-bottom: 2px;
        
        &::after {
          content: '';
          position: absolute;
          width: 0;
          height: 1px;
          bottom: 0;
          left: 0;
          background: ${({ theme }) => theme.colors.primary};
          transition: width 0.3s ease;
        }

        &:hover {
          color: ${({ theme }) => theme.colors.primary};
          opacity: 1;
          
          &::after {
            width: 100%;
          }
        }
      }
    }
  }


  .footer-contact {
    ul {
      li {
        display: flex;
        align-items: flex-start;
        color: ${({ theme }) => theme.colors.light};
        line-height: 1.6;
        margin-bottom: 1rem;
        
        .contact-icon {
          margin-right: 12px;
          color: ${({ theme }) => theme.colors.primary};
          font-size: 1.1rem;
          margin-top: 3px;
          flex-shrink: 0;
        }
        
        a, span {
          color: ${({ theme }) => theme.colors.light};
          opacity: 0.8;
          text-decoration: none;
          transition: all 0.3s ease;
          
          &:hover {
            color: ${({ theme }) => theme.colors.primary};
            opacity: 1;
          }
        }
      }
    }
  }


  .social-links {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;

    .social-link {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 45px;
      height: 45px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.1);
      color: ${({ theme }) => theme.colors.light};
      font-size: 1.2rem;
      transition: all 0.3s ease;
      border: 1px solid rgba(255, 255, 255, 0.1);

      &:hover {
        background: ${({ theme }) => theme.colors.primary};
        color: white;
        transform: translateY(-3px) scale(1.05);
        box-shadow: 0 5px 15px rgba(37, 99, 235, 0.3);
      }
    }
  }


  .footer-bottom {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1.5rem;
    position: relative;
    z-index: 1;
    margin-top: 2rem;

    p {
      margin: 0;
      color: ${({ theme }) => theme.colors.light};
      opacity: 0.8;
      font-size: 0.95rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      flex-direction: column;
      text-align: center;
    }
  }


  .footer-legal {
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      margin-top: 1rem;
    }

    a {
      color: inherit;
      text-decoration: none;
      font-size: 0.9rem;
      transition: color 0.3s ease;

      &:hover {
        color: ${({ theme }) => theme.colors.primary};
      }
    }


    span {
      opacity: 0.5;
    }
  }
`;

export default Footer;
